import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, PlayCircle, Utensils, BookOpen } from 'lucide-react';
import { ActivitySelector } from './activities/ActivitySelector';
import { FoodLogger } from './nutrition/FoodLogger';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDashboardStore } from '../store/dashboardStore';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => void;
}

export function FloatingMenu() {
  const [activeModal, setActiveModal] = useState<'start' | 'food' | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isEditMode } = useDashboardStore();

  const menuItems: MenuItem[] = [
    {
      id: 'start-activity',
      label: 'Start Activity',
      icon: <PlayCircle className="w-4 h-4" />,
      action: () => setActiveModal('start')
    },
    {
      id: 'food',
      label: 'Log Food',
      icon: <Utensils className="w-4 h-4" />,
      action: () => setActiveModal('food')
    },
    {
      id: 'journal',
      label: 'Journal',
      icon: <BookOpen className="w-4 h-4" />,
      action: () => navigate('/journal')
    }
  ];

  const isOverviewTab = location.pathname === '/home';

  return (
    <>
      {!activeModal && (
        <div className={`fixed right-6 z-60 flex flex-col items-end space-y-2 transition-all duration-300 ${
          isEditMode ? 'bottom-36' : 'bottom-20'
        }`}>
          {/* Submenu Items */}
          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="space-y-2"
              >
                {menuItems.map((item) => (
                  <motion.button
                    key={item.id}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    onClick={() => {
                      setShowMenu(false);
                      item.action();
                    }}
                    className="theme-button px-4 py-2 rounded-lg flex items-center gap-2 w-40 relative group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-primary opacity-0 group-hover:opacity-20 transition-opacity" />
                    {item.icon}
                    <span className="relative">{item.label}</span>
                  </motion.button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main Menu Button */}
          <div className="w-fit">
            <motion.button
              onClick={() => setShowMenu(!showMenu)}
              animate={{ rotate: showMenu ? 45 : 0 }}
              className="theme-button p-3 rounded-full shadow-lg relative group"
            >
              <Plus className="w-5 h-5" />
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.button>
          </div>
        </div>
      )}

      {/* Modals */}
      {activeModal === 'start' && (
        <ActivitySelector onClose={() => setActiveModal(null)} />
      )}
      {activeModal === 'food' && (
        <FoodLogger onClose={() => setActiveModal(null)} />
      )}
    </>
  );
}